import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { toast } from "sonner";

export default function SubscriptionsPage() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [planCode, setPlanCode] = useState("");
  const [packCode, setPackCode] = useState("");

  useEffect(() => {
    api.get("/v1/tenants").then((r) => setTenants(r.data.data.items)).catch(() => {});
    api.get("/v1/plans").then((r) => setPlans(r.data.data)).catch(() => {});
    api.get("/v1/industry-packs").then((r) => setPacks(r.data.data.items)).catch(() => {});
  }, []);

  const loadTenant = async (id) => {
    setTenantId(id);
    if (!id) { setSub(null); setHistory([]); return; }
    try {
      setSub((await api.get(`/v1/subscriptions/tenant/${id}`)).data.data);
      setHistory((await api.get(`/v1/subscriptions/tenant/${id}/history`)).data.data);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const act = async (path, body, label) => {
    try {
      const r = await api.post(`/v1/subscriptions/${path}`, body);
      toast.success(r.data.message || label);
      loadTenant(tenantId);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="subscriptions-page">
      <PageHeader title="Subscription Management" subtitle="Assign, upgrade, downgrade, renew & manage industry packs" />
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-1">Select Tenant</label>
        <select value={tenantId} onChange={(e) => loadTenant(e.target.value)} className="w-full md:w-96 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="sub-tenant-select">
          <option value="">Choose a tenant…</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName} ({t.tenantCode})</option>)}
        </select>
      </div>

      {sub && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-4">Current Subscription</h3>
            <div className="space-y-2 text-sm mb-5">
              <Row k="Plan" v={<Badge color="teal">{sub.planCode}</Badge>} />
              <Row k="Status" v={<Badge color={sub.status === "Active" ? "green" : "amber"}>{sub.status}</Badge>} />
              <Row k="Billing" v={sub.billingCycle} />
              <Row k="Industry Packs" v={(sub.industryPackCodes || []).join(", ") || "None"} />
            </div>
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <div className="flex gap-2">
                <select value={planCode} onChange={(e) => setPlanCode(e.target.value)} className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="sub-plan-select">
                  <option value="">Select plan…</option>
                  {plans.map((p) => <option key={p.planCode} value={p.planCode}>{p.planName}</option>)}
                </select>
                <button onClick={() => act("upgrade", { tenantId, planCode }, "Upgraded")} disabled={!planCode} className="px-3 py-2 text-sm rounded-lg bg-primary text-white font-medium disabled:opacity-50" data-testid="sub-upgrade">Upgrade</button>
                <button onClick={() => act("downgrade", { tenantId, planCode }, "Downgraded")} disabled={!planCode} className="px-3 py-2 text-sm rounded-lg border border-slate-300 font-medium disabled:opacity-50" data-testid="sub-downgrade">Downgrade</button>
              </div>
              <div className="flex gap-2">
                <button onClick={() => act("renew", { tenantId, planCode: sub.planCode }, "Renewed")} className="px-3 py-2 text-sm rounded-lg border border-slate-300 font-medium" data-testid="sub-renew">Renew</button>
                <button onClick={() => act("suspend", { tenantId, planCode: sub.planCode }, "Suspended")} className="px-3 py-2 text-sm rounded-lg border border-amber-300 text-amber-600 font-medium" data-testid="sub-suspend">Suspend</button>
              </div>
              <div className="flex gap-2">
                <select value={packCode} onChange={(e) => setPackCode(e.target.value)} className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="sub-pack-select">
                  <option value="">Select industry pack…</option>
                  {packs.map((p) => <option key={p.packCode} value={p.packCode}>{p.packName}</option>)}
                </select>
                <button onClick={() => act("industry-pack/assign", { tenantId, packCode }, "Pack assigned")} disabled={!packCode} className="px-3 py-2 text-sm rounded-lg bg-secondary text-white font-medium disabled:opacity-50" data-testid="sub-pack-assign">Assign</button>
                <button onClick={() => act("industry-pack/remove", { tenantId, packCode }, "Pack removed")} disabled={!packCode} className="px-3 py-2 text-sm rounded-lg border border-slate-300 font-medium disabled:opacity-50" data-testid="sub-pack-remove">Remove</button>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Subscription History</h3>
            <DataTable testid="sub-history-table" rows={history} loading={false}
              columns={[
                { key: "occurredAt", header: "Time", render: (h) => (h.occurredAt || "").slice(0, 19).replace("T", " ") },
                { key: "eventType", header: "Event", render: (h) => <Badge>{h.eventType}</Badge> },
                { key: "fromPlan", header: "From", render: (h) => h.fromPlan || "—" },
                { key: "toPlan", header: "To", render: (h) => h.toPlan || "—" },
              ]} />
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>
);
